import React, { useEffect, useState } from 'react';
import { MainLayout } from './components/layout/MainLayout';
import { Homepage } from './pages/Homepage';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Matters } from './pages/Matters';
import { MatterWorkspace } from './pages/MatterWorkspace';
import { Search } from './pages/Search';
import { Workflows } from './pages/Workflows';
import { MedicalChronology } from './pages/MedicalChronology';
import { DraftingCopilot } from './pages/DraftingCopilot';
import { ReviewQueue } from './pages/ReviewQueue';
import { Admin } from './pages/Admin';
import { Scale, Lock, ShieldCheck, AlertCircle } from 'lucide-react';
export type PersonaId = 'EF' | 'AT' | 'MS' | 'RK';
type Page = 'home' | 'login' | 'dashboard' | 'matters' | 'workspace' | 'search' | 'workflows' | 'chronology' | 'copilot' | 'review' | 'admin';
// ── Site Password Gate ──────────────────────────────────────────
const SITE_PASSWORD = 'Pendoah2026';
function PasswordGate({
  onAuthenticated
}: {
  onAuthenticated: () => void;
}) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === SITE_PASSWORD) {
      sessionStorage.setItem('site_auth', 'true');
      onAuthenticated();
    } else {
      setError(true);
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
    }
  };
  return <div className="min-h-screen w-full flex flex-col items-center justify-center bg-slate-900 p-4" data-id="element-0">
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-1">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-red-900/15 blur-[120px]" data-id="element-2" />
        <div className="absolute top-[60%] -right-[10%] w-[40%] h-[40%] rounded-full bg-slate-800/30 blur-[100px]" data-id="element-3" />
      </div>

      <div className="relative z-10 w-full max-w-sm flex flex-col items-center" data-id="element-4">
        <div className="flex flex-col items-center mb-10 text-center" data-id="element-5">
          <div className="bg-red-700 p-3 rounded-xl mb-4 shadow-lg shadow-red-900/50" data-id="element-6">
            <Scale className="w-8 h-8 text-white" data-id="element-7" />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight" data-id="element-8">
            Festeryga Law
          </h1>
          <p className="text-slate-500 text-sm mt-1" data-id="element-9">
            Local Legal Intelligence Platform
          </p>
        </div>

        <div className="w-full bg-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-xl p-6 shadow-2xl" data-id="element-10">
          <div className="flex items-center justify-center mb-5" data-id="element-11">
            <div className="p-2.5 bg-slate-700/50 rounded-full" data-id="element-12">
              <Lock className="w-5 h-5 text-slate-400" data-id="element-13" />
            </div>
          </div>
          <h2 className="text-base font-semibold text-white text-center mb-1" data-id="element-14">
            Protected Access
          </h2>
          <p className="text-xs text-slate-400 text-center mb-6" data-id="element-15">
            This platform is confidential. Enter the access password to
            continue.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4" data-id="element-16">
            <div data-id="element-17">
              <input type="password" value={password} onChange={e => {
              setPassword(e.target.value);
              setError(false);
            }} placeholder="Enter access password" autoFocus className={`w-full h-11 px-4 rounded-lg border bg-slate-900/80 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:ring-2 transition-all ${error ? 'border-red-500 focus:ring-red-500/30' : 'border-slate-600 focus:ring-red-700/30 focus:border-red-700/50'} ${isShaking ? 'animate-[shake_0.5s_ease-in-out]' : ''}`} data-id="element-18" />
              {error && <p className="mt-2 text-xs text-red-400 flex items-center" data-id="element-19">
                  <AlertCircle className="w-3 h-3 mr-1.5" data-id="element-20" />
                  Incorrect password. Please try again.
                </p>}
            </div>
            <button type="submit" className="w-full h-11 bg-red-700 hover:bg-red-600 text-white text-sm font-semibold rounded-lg transition-colors shadow-md shadow-red-900/30 hover:shadow-lg hover:shadow-red-900/40" data-id="element-21">
              Access Platform
            </button>
          </form>
        </div>

        <div className="mt-8 flex items-center text-xs text-slate-600" data-id="element-22">
          <ShieldCheck className="w-3.5 h-3.5 mr-1.5 text-emerald-500/50" data-id="element-23" />
          End-to-end encrypted • Confidential
        </div>

        <p className="mt-4 text-[10px] text-slate-600 text-center" data-id="element-24">
          © {new Date().getFullYear()} Platform by{' '}
          <a href="https://pendoah.ai" target="_blank" rel="noopener noreferrer" className="text-red-400/70 hover:text-red-400 transition-colors" data-id="element-25">
            Pendoah.ai
          </a>
        </p>
      </div>
    </div>;
}
// ── Main App ────────────────────────────────────────────────────
export function App() {
  const [isGateAuthenticated, setIsGateAuthenticated] = useState(() => {
    return sessionStorage.getItem('site_auth') === 'true';
  });
  const [currentPage, setCurrentPage] = useState<Page>('home');
  const [persona, setPersona] = useState<PersonaId | null>(null);
  const [searchInitialQuery, setSearchInitialQuery] = useState<string>('');
  // Password gate — blocks everything until authenticated
  if (!isGateAuthenticated) {
    return <PasswordGate onAuthenticated={() => setIsGateAuthenticated(true)} data-id="element-26" />;
  }
  const handleNavigate = (page: string, query?: string) => {
    setCurrentPage(page as Page);
    if (query !== undefined) {
      setSearchInitialQuery(query);
    } else if (page !== 'search') {
      setSearchInitialQuery('');
    }
  };
  const handleLogin = (personaId: string) => {
    setPersona(personaId as PersonaId);
    switch (personaId) {
      case 'EF':
        setCurrentPage('dashboard');
        break;
      case 'AT':
        setCurrentPage('chronology');
        break;
      case 'MS':
        setCurrentPage('matters');
        break;
      case 'RK':
        setCurrentPage('workflows');
        break;
      default:
        setCurrentPage('dashboard');
    }
  };
  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <Dashboard onNavigate={handleNavigate} persona={persona} data-id="element-27" />;
      case 'matters':
        return <Matters onNavigate={handleNavigate} data-id="element-28" />;
      case 'workspace':
        return <MatterWorkspace onNavigate={handleNavigate} data-id="element-29" />;
      case 'search':
        return <Search initialQuery={searchInitialQuery} data-id="element-30" />;
      case 'workflows':
        return <Workflows onNavigate={handleNavigate} data-id="element-31" />;
      case 'chronology':
        return <MedicalChronology data-id="element-32" />;
      case 'copilot':
        return <DraftingCopilot data-id="element-33" />;
      case 'review':
        return <ReviewQueue data-id="element-34" />;
      case 'admin':
        return <Admin data-id="element-35" />;
      default:
        return <Dashboard onNavigate={handleNavigate} data-id="element-36" />;
    }
  };
  if (currentPage === 'home') {
    return <Homepage onNavigate={handleNavigate} data-id="element-37" />;
  }
  if (currentPage === 'login') {
    return <Login onLogin={handleLogin} data-id="element-38" />;
  }
  return <MainLayout currentPage={currentPage} onNavigate={handleNavigate} persona={persona} data-id="element-39">
      {renderPage()}
    </MainLayout>;
}